import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { createGSAPContext } from "../hooks/useGSAPCleanup";
import "./SectionEvidence.css";
import Spacer from "./common/Spacer";

gsap.registerPlugin(ScrollTrigger);

function SectionEvidence() {
  const sectionRef = useRef(null);
  const statNumber1Ref = useRef(null);
  const statNumber2Ref = useRef(null);
  const statNumber3Ref = useRef(null);
  const statNumber4Ref = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) return;

    const ctx = createGSAPContext(() => {
      // Main title animation
      gsap.from(".evidence-title", {
        scrollTrigger: {
          trigger: section,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
        opacity: 0,
        y: 50,
        duration: 1,
        ease: "power2.out",
      });

      // Stats animation
      const statsTl = gsap.timeline({
        scrollTrigger: {
          trigger: ".stats-container",
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      statsTl
        .from(".stat-card", {
          opacity: 0,
          y: 50,
          scale: 0.9,
          duration: 0.8,
          stagger: 0.2,
          ease: "back.out(1.2)",
        })
        .from(
          ".principle-card",
          {
            opacity: 0,
            x: -30,
            duration: 0.6,
            stagger: 0.15,
            ease: "power2.out",
          },
          "-=0.4"
        );

      // Number counter animation
      const counter1 = { value: 0 };
      const counter2 = { value: 0 };
      const counter3 = { value: 0 };
      const counter4 = { value: 0 };

      ScrollTrigger.create({
        trigger: ".stats-container",
        start: "top 70%",
        onEnter: () => {
          gsap.to(counter1, {
            value: 3,
            duration: 2,
            ease: "power2.out",
            onUpdate: () => {
              if (statNumber1Ref.current) {
                statNumber1Ref.current.textContent = Math.floor(counter1.value);
              }
            },
          });

          gsap.to(counter2, {
            value: 22,
            duration: 2,
            ease: "power2.out",
            onUpdate: () => {
              if (statNumber2Ref.current) {
                statNumber2Ref.current.textContent = Math.floor(counter2.value);
              }
            },
          });

          gsap.to(counter3, {
            value: 6,
            duration: 2,
            ease: "power2.out",
            onUpdate: () => {
              if (statNumber3Ref.current) {
                statNumber3Ref.current.textContent = Math.floor(counter3.value);
              }
            },
          });

          gsap.to(counter4, {
            value: 9333,
            duration: 2.5,
            ease: "power2.out",
            onUpdate: () => {
              if (statNumber4Ref.current) {
                statNumber4Ref.current.textContent = Math.floor(
                  counter4.value
                ).toLocaleString();
              }
            },
          });
        },
        once: true,
      });

      // Conclusion animation
      gsap.from(".evidence-conclusion", {
        scrollTrigger: {
          trigger: ".evidence-conclusion",
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
        opacity: 0,
        scale: 0.95,
        duration: 1,
        ease: "power2.out",
      });
    }, section);

    return () => {
      if (ctx && ctx.cleanup) {
        ctx.cleanup();
      }
    };
  }, []);

  return (
    <section className="section section-evidence" ref={sectionRef}>
      <div className="container">
        <h2 className="evidence-title">
          고객 중심? 그거 순진한 소리 아니에요?
        </h2>

        <div className="principles-section">
          <div className="principles-list">
            <div className="principle-item">
              <p className="principle-company">StoryBrand 핵심</p>
              <p className="principle-quote">
                "고객을 주인공으로, 기업을 조력자로"
              </p>
              <p className="principle-evidence">
                적용 기업 평균 매출 2-3배 증가
              </p>
              <p className="principle-context">10,000개 이상 기업이 도입</p>
            </div>
            <div className="principle-item">
              <p className="principle-company">Amazon 제1원칙</p>
              <p className="principle-quote">
                "고객 집착 (Customer Obsession)"
              </p>
              <p className="principle-evidence">
                1997년 주주서한부터 2024년까지 변하지 않는 제1원칙
              </p>
              <p className="principle-context">시가총액 2조 달러 도달</p>
            </div>
            <div className="principle-item">
              <p className="principle-company">HubSpot 인바운드 마케팅</p>
              <p className="principle-quote">"고객에게 먼저 가치를 제공하라"</p>
              <p className="principle-evidence">
                연매출 3억원 → 2.8조원 성장 (2006-2023)
              </p>
              <p className="principle-context">
                B2B 마케팅 플랫폼 선두, 인바운드 마케팅 창시
              </p>
            </div>
            <div className="principle-item">
              <p className="principle-company">LinkedIn 통계</p>
              <p className="principle-quote">"고객 중심 기업의 성과"</p>
              <p className="principle-evidence">
                고객 중심 기업이 그렇지 않은 기업보다 60% 더 높은 수익성
              </p>
              <p className="principle-context">
                2023년 글로벌 B2B 마케팅 리서치
              </p>
            </div>
          </div>
        </div>

        <div className="philosophy-insight">
          <p className="insight-text">
            성공 기업들의 공통점.
            <br />
            <span className="insight-emphasis">
              고객이 주인공, 기업은 조력자.
            </span>
            <br />
            이게 성장의 본질 아닐까요?
          </p>
        </div>
        <Spacer size={50} />
      </div>
    </section>
  );
}

export default SectionEvidence;
